import { useAsync } from 'react-async';
import { getRoomsAvailability } from 'api/hotels';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RoomItem from './RoomItem';
import HotelBookedSummary from './HotelBookedSummary';
import { useBooking, useExperienceBooking, useLanguage } from 'hooks';
import { Stack } from '@koob/margaret';
import { Text } from 'ui';
import { KoobButton, KoobPlaceholder } from '@koob/ui';
import { Modal } from 'components';
import { cloneDeep, omit } from 'lodash';
import { KOEDIA } from '../../../constants';
import formatHotelRoomPossibilities from '@koob/checkout/src/utilities/formatHotelRoomPossibilities';
import {
  clearOfferedRoomSupplementKeys,
  isSameOfferedRoom,
  selectedRoomSupplementKey,
  shiftSelectedRoomSupplementKeys,
} from '@koob/checkout';
import { SupplierEnum } from '@koob/enums';
import {
  getStayFlexibleRequirements,
  toOptionalSupplementOptions,
  isSameBedComposition,
} from '@koob/utils';
import { StayFlexibleRequirements } from '@koob/booking';

const getRoomsAvailable = async ({ id, filters, locale }) => {
  // Re-wrap the normalized { requestId, expiresAt, possibilities } envelope as
  // { data: envelope } — the shape this component already reads from.
  const envelope = await getRoomsAvailability({ id, filters, locale });
  return { data: envelope };
};

export const getCorrectedRooms = (selectedRooms, currentSelectedRoom) => {
  const isKoediaSupplier =
    selectedRooms?.[0]?.supplier?.toUpperCase() === KOEDIA;

  if (!isKoediaSupplier || selectedRooms.length === 0) {
    return selectedRooms;
  }

  const allRooms = [
    ...selectedRooms.map(r => ({ ...r, bed: { ...r.bed } })),
    currentSelectedRoom,
  ];

  for (let i = allRooms.length - 2; i >= 0; i--) {
    const nextRoom = allRooms[i + 1];
    const compatEntry = allRooms[i].compatibleRoomCompositions?.find(
      entry => entry.bedChoices?.[0]?.id === nextRoom.bed?.id,
    );
    if (compatEntry?._sourceBedId) {
      allRooms[i] = {
        ...allRooms[i],
        bed: { ...allRooms[i].bed, id: compatEntry._sourceBedId },
      };
    }
  }

  return allRooms.slice(0, -1);
};

const RoomCompositionContent = ({
  isLoading,
  selectedRooms,
  availableRooms,
  deleteRoom,
  hotel,
  currentIndex,
  handleRoomIndexChange,
  filters,
  totalRoomNumber,
  currentSelectedRoom,
  validateRoom,
  isOtherProposal,
  selectedRoomsFromPromotionSelect,
  requestId,
  promoList,
  updateRoomSupplements,
  bookedRoom,
}) => {
  const { t } = useTranslation('booking');

  const [selectedPromotionSupplements, setSelectedPromotionSupplements] =
    useState({});
  const [currentPossibilityIndex, setCurrentPossibilityIndex] = useState(0);

  // Removing a validated room shifts every slot after it down. The supplement
  // store is keyed by slot, so its keys have to move with them: left alone, the
  // room that takes the freed slot shows — and, on the next tick, books — the
  // supplements of the room that was removed. `deleteRoom` also drops whatever
  // was being ticked on an offered card, so those entries go too.
  const deleteSelectedRoom = roomIndex => {
    setSelectedPromotionSupplements(prev =>
      clearOfferedRoomSupplementKeys(
        shiftSelectedRoomSupplementKeys(prev, roomIndex),
      ),
    );
    deleteRoom(roomIndex);
  };

  // Validating a room ends the selection that was being made: it keeps its
  // supplements through its own `optionalPromos`, and the ticks left on the
  // offered card it came from must not survive into the next slot, which offers
  // that same room type under that same key. `validateRoom` resets the pending
  // selection for the same reason.
  const validateSelectedRoom = (...args) => {
    setSelectedPromotionSupplements(clearOfferedRoomSupplementKeys);
    validateRoom(...args);
  };

  // Only meaningful on the other-proposal path: validated rooms are hidden
  // there, so the room being swapped in is the whole selection.
  const otherProposalStayFlexibleRequirements = getStayFlexibleRequirements(
    currentSelectedRoom ? [currentSelectedRoom] : [],
    { startAt: filters?.startAt, endAt: filters?.endAt },
  );
  // The currency a supplement sold as a flat amount is quoted in; the swapped-in
  // room is the whole selection here, so it is the one that carries it.
  const otherProposalCurrency = currentSelectedRoom?.bed?.currency;

  return (
    <div className="max-w-7xl mx-auto grid grid-cols-3 gap-5 items-start">
      <div className="col-span-2">
        <div className="flex-col space-y-5">
          {isLoading && (
            <>
              <KoobPlaceholder className="h-40 w-full rounded-lg" />
              <KoobPlaceholder className="h-40 w-full rounded-lg" />
              <KoobPlaceholder className="h-40 w-full rounded-lg" />
            </>
          )}

          {!isLoading &&
            selectedRooms.length === 0 &&
            (!availableRooms || availableRooms.length === 0) && (
              <div className="text-center text-gray-400">
                <i className="far fa-empty-set fa-2x" />

                <div className="mt-5">{t('noPossibilities')}</div>
              </div>
            )}

          {!isOtherProposal &&
            selectedRooms?.length > 0 &&
            selectedRooms.map((room, selectedRoomIndex) => (
              <RoomItem
                key={selectedRoomIndex}
                possibility={room}
                isSelected
                isRoomSelected
                filters={filters}
                deleteRoom={() => deleteSelectedRoom(selectedRoomIndex)}
                promotions={promoList}
                setSelectedPromotionSupplements={
                  setSelectedPromotionSupplements
                }
                selectedPromotionSupplements={selectedPromotionSupplements}
                // Two rooms of the same type share an originalIndex, so keying
                // the supplement store on it would make them overwrite each
                // other. A validated room is identified by its slot instead.
                supplementKey={selectedRoomSupplementKey(selectedRoomIndex)}
                updateRoomSupplements={supplements =>
                  updateRoomSupplements(selectedRoomIndex, supplements)
                }
              />
            ))}

          {!isOtherProposal && selectedRooms?.length > 0 && <hr />}

          {availableRooms?.length > 0 &&
            availableRooms.map((possibility, possibilityIndex) => {
              const isSelectedRoom =
                currentIndex !== undefined &&
                (currentIndex === possibility.originalIndex ||
                  possibility.variants
                    ?.map(variant => variant.originalIndex)
                    .includes(currentIndex) ||
                  possibility.originalIndex === currentPossibilityIndex);

              return (
                <RoomItem
                  key={possibilityIndex}
                  possibility={possibility}
                  selectedPossibilityIndex={currentIndex}
                  isSelected={isSelectedRoom}
                  // The card being chosen is priced from the room in progress:
                  // picking a promotion moves the room to another possibility,
                  // which this card's own `possibility` never becomes.
                  selectedRoom={
                    isSelectedRoom ? currentSelectedRoom : undefined
                  }
                  handleSelect={(possibilityIndex, bedIndex = 0) => {
                    const newPossibility =
                      possibility.variants?.find(
                        variant => variant.originalIndex === possibilityIndex,
                      ) ?? possibility;
                    // Moving to another room ends the selection the ticks below
                    // the previous card belonged to: the room in progress is
                    // rebuilt without them, so left in the store they would stay
                    // ticked on a card that is neither priced nor booked with
                    // them. A promotion or bed change stays on the same room and
                    // keeps them.
                    if (
                      !isSameOfferedRoom(currentSelectedRoom, newPossibility)
                    ) {
                      setSelectedPromotionSupplements(
                        clearOfferedRoomSupplementKeys,
                      );
                    }
                    handleRoomIndexChange(
                      possibilityIndex,
                      bedIndex,
                      newPossibility,
                    );
                    setCurrentPossibilityIndex(possibility.originalIndex);
                  }}
                  filters={filters}
                  promotions={promoList}
                  selectedRoomsFromPromotionSelect={
                    selectedRoomsFromPromotionSelect
                  }
                  setSelectedPromotionSupplements={
                    setSelectedPromotionSupplements
                  }
                  selectedPromotionSupplements={selectedPromotionSupplements}
                  selectedRoomsCount={selectedRooms?.length}
                  supplementKey={possibility.originalIndex}
                  bookedRoom={bookedRoom}
                />
              );
            })}

          {/*
            KD-3496 — swapping a room for another proposal skips the summary
            below, and with it the extra-nights note. The swap is a room
            selection like any other and can carry an early check-in / late
            check-out, so the note is restated here; its confirm button is in
            the parent modal's footer, so it sits under the room list.
          */}
          {isOtherProposal && (
            <StayFlexibleRequirements
              requirements={otherProposalStayFlexibleRequirements}
              currency={otherProposalCurrency}
              translationKey="booking"
              keyPrefix="summary.stayFlexible"
              variant="info"
            />
          )}
        </div>
      </div>

      {!isOtherProposal && (
        <div className="sticky top-[120px] bg-red-50">
          <HotelBookedSummary
            startDate={filters.startAt}
            endDate={filters.endAt}
            hotel={hotel}
            isInfo={false}
            rooms={selectedRooms}
            roomsFilter={filters?.rooms}
            totalRoomNumber={totalRoomNumber}
            requestId={requestId}
            step={1}
            disabledButton={
              !Boolean(currentSelectedRoom) || currentIndex === undefined
            }
            handleNextStep={validateSelectedRoom}
          />
        </div>
      )}
    </div>
  );
};

const HotelRoomComposition = ({
  goInfosPage,
  hotel,
  booking,
  setIsOpen,
  filters: updateFilters,
  setFieldValue,
  selectedRoomIndex,
  roomComposition,
  setRequestId: setEditRoomRequestId,
}) => {
  const { t } = useTranslation('booking');
  const {
    filters: createFilters,
    setCurrentSelectedRoom,
    currentSelectedRoom,
    selectedRooms,
    setSelectedRooms,
    setRequestId,
    setExpirationDate,
    selectedSupplements,
    setSelectedSupplements,
  } = useBooking();
  const { basket, setBasket } = useExperienceBooking();
  const { lang } = useLanguage();

  const filters = updateFilters ?? createFilters;
  const { rooms } = filters;
  const [currentIndex, setCurrentIndex] = useState(undefined);
  const [possibilities, setPossibilities] = useState([]);
  const [initialPossibilities, setInitialPossibilities] = useState([]);
  const [bedCompositions, setBedCompositions] = useState(Array(rooms?.length));
  const { hotelId } = useParams();

  const totalRoomNumber = rooms?.length;
  const [newBedroom, setNewBedroom] = useState();

  // Supplements stay editable after a room is validated — an early check-in is
  // often decided once the rooms are laid out, and for a zero-priced one there
  // is nothing to re-quote. A validated room is no longer what
  // `currentSelectedRoom` tracks, so the change is written into its stored entry
  // directly; the price moves by the DELTA rather than being recomputed, which
  // keeps it right whatever was ticked before.
  const updateRoomSupplements = (roomIndex, supplements) => {
    setSelectedRooms(previousRooms =>
      previousRooms.map((room, index) => {
        if (index !== roomIndex) {
          return room;
        }
        const sum = list =>
          (list ?? []).reduce((total, { price }) => total + (price ?? 0), 0);
        const delta = sum(supplements) - sum(room.optionalPromos);

        return {
          ...room,
          optionalPromos: supplements,
          bed: {
            ...room.bed,
            totalPriceWithPromo: (room.bed?.totalPriceWithPromo ?? 0) + delta,
          },
        };
      }),
    );
  };

  const handleRoomIndexChange = (index, bedIndex, possibility) => {
    if (booking) {
      setNewBedroom({
        name: possibility.name,
        bedChoice: possibility.bedChoices[bedIndex],
        otherProposal: true,
      });
    }

    // The supplements ticked so far belong to the room they were ticked on. A
    // promotion or bed change stays on that room and keeps them; picking ANOTHER
    // room must not inherit them — its own select shows nothing ticked, yet the
    // room would be priced with them, warned about as an extra night, and
    // booked with them.
    const keepsSupplements = isSameOfferedRoom(
      currentSelectedRoom,
      possibility,
    );
    const roomSupplements = keepsSupplements ? (selectedSupplements ?? []) : [];
    if (!keepsSupplements && selectedSupplements?.length > 0) {
      setSelectedSupplements([]);
    }

    const promosPrice = roomSupplements.reduce(
      (total, { price }) => total + price,
      0,
    );

    setCurrentSelectedRoom({
      ...possibility,
      bed: {
        ...possibility.bedChoices[bedIndex],
        totalPriceWithPromo:
          possibility?.bedChoices[bedIndex]?.totalPriceWithPromo + promosPrice,
      },
      optionalPromos: roomSupplements,
    });

    setCurrentIndex(index);
  };

  const formatDataBasedOnSelection = (selectedRooms, possibility) => {
    let formattedData = {};

    for (let i = 0; i < selectedRooms.length; i++) {
      if (!possibility) {
        return formattedData;
      }

      formattedData = {
        possibilities: possibility?.compatibleRoomCompositions,
      };
    }

    return formattedData;
  };

  const validateRoom = hotelBasketRoomCount => {
    const roomCount = Number.isInteger(hotelBasketRoomCount)
      ? hotelBasketRoomCount
      : totalRoomNumber;
    if (roomCount !== selectedRooms.length + 1) {
      setSelectedRooms(prev => [...prev, currentSelectedRoom]);
      setCurrentSelectedRoom(undefined);
      setCurrentIndex(undefined);
      // The confirmed room keeps its own supplements; the shared selection has
      // to start empty for the next slot, or that slot inherits them — priced
      // in, and warned about as an extra night it never asked for.
      setSelectedSupplements([]);
      return;
    }

    const correctedRooms = getCorrectedRooms(
      selectedRooms,
      currentSelectedRoom,
    );

    setSelectedRooms([...correctedRooms, currentSelectedRoom]);
    goInfosPage();
  };

  const deleteRoomFromBasket = roomIndex => {
    if (!basket) {
      return;
    }

    const basketHotelIndex = basket?.findIndex(
      basketExperience => basketExperience.internalId === filters.internalId,
    );
    if (basketHotelIndex !== -1) {
      const updatedRooms = basket[basketHotelIndex]?.selectedRooms.filter(
        (_room, index) => index !== roomIndex,
      );
      const updatedBasket = [...basket];
      updatedBasket[basketHotelIndex] = {
        ...updatedBasket[basketHotelIndex],
        selectedRooms: updatedRooms,
      };
      setBasket(updatedBasket);
    }
  };

  const deleteRoom = roomIndex => {
    setSelectedRooms(prev => prev.filter((r, i) => i !== roomIndex));
    setCurrentIndex(undefined);
    setCurrentSelectedRoom(undefined);
    // Same reason as in validateRoom: whatever the removed room carried must
    // not seed the next selection.
    setSelectedSupplements([]);
    deleteRoomFromBasket(roomIndex);
  };

  const { data, isLoading } = useAsync(
    {
      promiseFn: getRoomsAvailable,
      id: booking?.hotel?.id ?? hotelId,
      locale: lang,
      filters: {
        bookingId: filters.bookingId,
        startAt: filters.startAt,
        endAt: filters.endAt,
        templateId: filters?.templateId,
        rooms: filters?.rooms?.map(room => ({
          ...omit(room, 'children'),
          firstAdultNationality: room?.firstAdultNationality?.value,
        })),
      },
    },
    false,
  );
  // Every supplement of the availability, all rooms together: the cards use it
  // to keep supplement NAMES out of the promotion select. What a supplement
  // costs is never read from here — it is priced off the bed being shown, since
  // a percentage supplement is worth a different amount on each room.
  const promoList = (data?.data?.possibilities || []).flatMap(choice =>
    (choice?.bedChoices ?? []).flatMap(bedChoice =>
      toOptionalSupplementOptions(bedChoice?.optionalSupplements),
    ),
  );

  function groupPossibilities(data) {
    const filteredPossibilities = [];
    const is_supplement = ['supplement', 'supplement_percent'];
    const datas = cloneDeep(data.possibilities);
    const originalPossibilities = datas
      .sort((a, b) => {
        const hasPromoA = !!a.bedChoices?.[0]?.promotions?.[0];
        const hasPromoB = !!b.bedChoices?.[0]?.promotions?.[0];
        return hasPromoB - hasPromoA;
      })
      .map((possibility, index) => {
        possibility.originalIndex = index;
        if (possibility.supplier === SupplierEnum.KOOB) {
          filteredPossibilities.push(possibility);
        }

        return possibility;
      });

    const groupedPossibilities = {};

    filteredPossibilities.forEach(possibility => {
      const key = `${possibility?.supplierSpecificInformations?.hotelRoomId}`;
      if (!groupedPossibilities[key]) {
        groupedPossibilities[key] = {
          ...possibility,
          allPromotions: {},
          originalIndexes: [],
        };
      }
      possibility.bedChoices.forEach(bedChoice => {
        if (!is_supplement.includes(bedChoice.promotionInfo?.kind)) {
          // Prefix the promo id so the outer key is not an integer-like
          // string, otherwise Object.values() would auto-sort numerically
          // and break the API ordering (which puts cumulative combos first).
          const promoKey = `p${bedChoice.id.split('##').pop()}`;
          if (!groupedPossibilities[key].allPromotions[promoKey]) {
            groupedPossibilities[key].allPromotions[promoKey] = [
              { [bedChoice.id]: bedChoice.promotions },
            ];
          } else {
            groupedPossibilities[key].allPromotions[promoKey].push({
              [bedChoice.id]: bedChoice.promotions,
            });
          }
          if (
            groupedPossibilities[key].originalIndexes.indexOf(
              possibility.originalIndex,
            ) === -1
          ) {
            groupedPossibilities[key].originalIndexes.push(
              possibility.originalIndex,
            );
          }
        }
      });
    });

    const newPossibilities = Object.values(groupedPossibilities).map(
      possibility => {
        const allPromotions = Object.values(possibility.allPromotions).flat();
        delete possibility.allPromotions;
        return { ...possibility, allPromotions };
      },
    );

    newPossibilities.forEach(possibility => {
      originalPossibilities.forEach(originalPossibility => {
        if (
          possibility.originalIndexes.includes(
            originalPossibility.originalIndex,
          )
        ) {
          originalPossibility.allPromotions = possibility.allPromotions;
        }
      });
    });

    const remainingPossibilitiesKoedia = originalPossibilities.filter(
      possibility => possibility.supplier === SupplierEnum.KOEDIA,
    );

    const remainingPossibilitiesRakuten = originalPossibilities.filter(
      possibility => possibility.supplier === SupplierEnum.RAKUTEN,
    );

    const remainingPossibilitiesStaah = originalPossibilities.filter(
      possibility => possibility.supplier === SupplierEnum.STAAH,
    );
    // Channel-manager rooms (flat list, no compatible compositions), same
    // handling as Staah.
    const remainingPossibilitiesTlLincoln = originalPossibilities.filter(
      possibility => possibility.supplier === SupplierEnum.TL_LINCOLN,
    );

    const wrappedRemainingPossibilitiesKoedia = wrapAvailableRoomPossibilities(
      remainingPossibilitiesKoedia,
    );

    updateOriginalCompatibilityRoom(
      originalPossibilities,
      originalPossibilities,
    );

    const newOriginalPossibilities = [
      ...newPossibilities,
      ...remainingPossibilitiesRakuten,
      ...wrappedRemainingPossibilitiesKoedia,
      ...remainingPossibilitiesStaah,
      ...remainingPossibilitiesTlLincoln,
    ].sort((a, b) => a.originalIndex - b.originalIndex);

    const updatedPossibilitiesList = newOriginalPossibilities.map(item => {
      const composition = item;
      composition?.bedChoices.forEach(bedChoice => {
        if (is_supplement.includes(bedChoice?.promotionInfo?.kind)) {
          bedChoice.totalPriceWithPromo = bedChoice.totalPriceWithoutPromo;
        }
      });
      return composition;
    });
    const cleanedPossibilities = removeDuplicateRooms(updatedPossibilitiesList);
    return { original: originalPossibilities, new: cleanedPossibilities };
  }

  const tagsKey = tags => {
    return tags
      ?.map(t => t.label?.toLowerCase()?.replace(/ /g, ''))
      ?.sort()
      ?.join('#');
  };

  const getPromotionsKey = (promos = []) => {
    const sorted = promos
      .map(promotion => promotion.toLowerCase().replace(/\s+/g, ''))
      .sort();

    return JSON.stringify(sorted);
  };

  const areCompositionsEqual = useCallback((composition1, composition2) => {
    const isSameCompositions =
      composition1.name === composition2.name &&
      composition1.supplierCode === composition2.supplierCode &&
      composition1.source === composition2.source &&
      composition1.bedChoices[0].totalPriceWithPromo ===
        composition2.bedChoices[0].totalPriceWithPromo &&
      tagsKey(composition1.bedChoices[0].tags) ===
        tagsKey(composition2.bedChoices[0].tags) &&
      getPromotionsKey(composition1.bedChoices[0]?.promotions) ===
        getPromotionsKey(composition2.bedChoices[0]?.promotions);

    return isSameCompositions;
  }, []);

  const getRoomKey = useCallback((room, depth) => {
    return `${room.name}_${room.supplierCode || room.supplier}_${room.source}_${
      room.bedChoices[0].totalPriceWithPromo
    }_${depth}_${tagsKey(room.bedChoices[0].tags)}_${getPromotionsKey(room.bedChoices[0]?.promotions)}`;
  }, []);

  const mergeCompatibleRoomCompositions = useCallback(
    (targetRoom, compatibleRoomCompositions, depth, sourceBedId = null) => {
      targetRoom.compatibleRoomCompositions =
        targetRoom.compatibleRoomCompositions || [];
      (compatibleRoomCompositions || []).forEach(composition => {
        const existingComposition = targetRoom.compatibleRoomCompositions.find(
          existingComposition =>
            areCompositionsEqual(existingComposition, composition),
        );
        if (!existingComposition) {
          targetRoom.compatibleRoomCompositions.push({
            ...composition,
            ...(sourceBedId ? { _sourceBedId: sourceBedId } : {}),
          });
        } else if (depth > 0) {
          mergeCompatibleRoomCompositions(
            existingComposition,
            composition.compatibleRoomCompositions,
            depth - 1,
            sourceBedId,
          );
        }
      });
    },
    [areCompositionsEqual],
  );

  const wrapAvailableRoomCompositions = useCallback(
    (mergedRooms, room, depth) => {
      const key = getRoomKey(room, depth);
      if (!mergedRooms.has(key)) {
        mergedRooms.set(key, { ...room });
      } else {
        mergeCompatibleRoomCompositions(
          mergedRooms.get(key),
          room.compatibleRoomCompositions,
          depth,
          room.bedChoices[0]?.id,
        );
      }
      if (depth > 0) {
        (room.compatibleRoomCompositions || []).forEach(composition => {
          wrapAvailableRoomPossibilities(
            composition.compatibleRoomCompositions || [],
          );
        });
      }
    },
    // eslint-disable-next-line
    [mergeCompatibleRoomCompositions, getRoomKey],
  );

  const wrapAvailableRoomPossibilities = useCallback(
    availableRooms => {
      const mergedRooms = new Map();
      availableRooms.forEach(room => {
        wrapAvailableRoomCompositions(mergedRooms, room, rooms?.length - 1);
      });
      return Array.from(mergedRooms.values());
    },
    [wrapAvailableRoomCompositions],
  );

  const removeDuplicateRooms = rooms => {
    const uniqueRoomsIndex = {};
    return rooms.filter(room => {
      const key = `${room.name}_${
        room?.supplierCode ? room?.supplierCode : room?.supplier
      }_${room.source}_${room.bedChoices[0].totalPriceWithPromo}_${tagsKey(
        room.bedChoices[0].tags,
      )}_${getPromotionsKey(room.bedChoices[0]?.promotions)}`;

      if (!uniqueRoomsIndex[key]) {
        uniqueRoomsIndex[key] = true;
        return true;
      }
      return false;
    });
  };

  const updateOriginalCompatibilityRoom = (rooms, referencesRooms) => {
    rooms.forEach(room => {
      if (room.compatibleRoomCompositions?.length > 0) {
        updateOriginalCompatibilityRoom(
          room.compatibleRoomCompositions,
          referencesRooms,
        );
      }

      if (room.originalIndex === undefined) {
        if (room.supplier !== SupplierEnum.KOOB) {
          const originalRoom = referencesRooms.find(rRef => {
            if (rRef.supplier === SupplierEnum.KOOB) {
              return false;
            }
            return (
              rRef.name === room.name &&
              rRef.supplier === room.supplier &&
              rRef.source === room.source
            );
          });
          if (originalRoom) {
            room.originalIndex = originalRoom.originalIndex;
          }
        } else {
          const originalRoom = referencesRooms.find(rRef => {
            if (rRef.supplier !== SupplierEnum.KOOB) {
              return false;
            }
            const rBedsIds = rRef.bedChoices
              .map(bed => {
                return `${bed.promotions.join('_')}`;
              })
              .join('_');
            const bedIds = room.bedChoices
              .map(bed => {
                return `${bed.promotions.join('_')}`;
              })
              .join('_');

            return (
              rRef.supplierSpecificInformations.hotelRoomId ===
                room.supplierSpecificInformations.hotelRoomId &&
              rBedsIds === bedIds
            );
          });

          room.originalIndex = originalRoom?.originalIndex;
          room.allPromotions = originalRoom?.allPromotions;
          room.allOptionalSupplements = originalRoom?.allOptionalSupplements;
        }
      }
    });
  };

  function selectedRoomsFromPromotionSelect(
    originalIndex,
    bedChoicePromo,
    bedName,
  ) {
    const room = initialPossibilities
      .filter(possibility => {
        return possibility.originalIndex === originalIndex;
      })
      .pop();
    const rooms = initialPossibilities.filter(possibility => {
      return (
        possibility?.supplierSpecificInformations?.hotelRoomId ===
        room?.supplierSpecificInformations?.hotelRoomId
      );
    });

    let found = false;
    for (let possibility of rooms) {
      if (found) {
        break;
      }
      for (let i = 0; i < possibility.bedChoices.length; i++) {
        let bed = possibility.bedChoices[i];
        if (
          bed.name === bedName &&
          bed.promotions.join(', ') === bedChoicePromo
        ) {
          handleRoomIndexChange(possibility.originalIndex, i, possibility);
          found = true;
          break;
        }
      }
    }
  }

  useEffect(() => {
    const isKoediaSupplier =
      selectedRooms?.[0]?.supplier === SupplierEnum.KOEDIA;
    const isRakutenSupplier =
      selectedRooms?.[0]?.supplier === SupplierEnum.RAKUTEN;
    const isStaahSupplier =
      selectedRooms?.[0]?.supplier === SupplierEnum.STAAH ||
      selectedRooms?.[0]?.supplier === SupplierEnum.TL_LINCOLN;
    // Own inventory (KOOB) drops nested compositions: the next slot's rooms come
    // from the same flat list, filtered by capacity at render time, so there is
    // nothing to re-derive after a selection.
    const isOwnInventory = selectedRooms?.[0]?.supplier === SupplierEnum.KOOB;
    if (data && data.data && selectedRooms.length === 0) {
      const response = cloneDeep(data?.data);
      const groupedPossibilities = groupPossibilities(response);
      setPossibilities(groupedPossibilities.new);
      setInitialPossibilities(groupedPossibilities.original);

      setRequestId(data.data.requestId);
      if (setEditRoomRequestId) {
        setEditRoomRequestId(data.data.requestId);
      }

      setExpirationDate(data.data.expiresAt);
    }

    const formatDataInput = isKoediaSupplier ? possibilities : selectedRooms;
    if (selectedRooms.length > 0 && !isOwnInventory) {
      const response =
        isKoediaSupplier || isRakutenSupplier || isStaahSupplier
          ? isKoediaSupplier
            ? formatDataInput
            : possibilities
          : cloneDeep(data?.data)?.possibilities;

      const possibility = response?.find(item => {
        const lastSelectedRoom = selectedRooms.at(-1);

        return (
          item.name === lastSelectedRoom.name &&
          (!isKoediaSupplier ||
            (tagsKey(item.bedChoices[0].tags) ===
              tagsKey(lastSelectedRoom.bedChoices[0].tags) &&
              getPromotionsKey(item.bedChoices[0].promotions) ===
                getPromotionsKey(lastSelectedRoom.bedChoices[0].promotions)))
        );
      });

      if (possibility) {
        const updatedPossibilities = formatDataBasedOnSelection(
          formatDataInput,
          possibility,
        );
        const groupedPossibilities = groupPossibilities(updatedPossibilities);

        setPossibilities(groupedPossibilities.new);
        setInitialPossibilities(groupedPossibilities.original);
        setRequestId(data.data.requestId);
        setExpirationDate(data.data.expiresAt);
      }
    }
  }, [data, selectedRooms]);

  useEffect(() => {
    const draftBedCompositions = [...bedCompositions];
    draftBedCompositions[currentIndex] = null;
    setBedCompositions(draftBedCompositions);
  }, [currentIndex]);

  // Own inventory exposes minCapacity/maxCapacity instead of nested
  // compositions: for each slot only offer rooms that fit its occupancy. Rooms
  // without capacity fields (third-party) always pass.
  const currentSlot = rooms?.[selectedRooms.length];
  const currentSlotPax =
    (currentSlot?.adults ?? 0) + (currentSlot?.childrenBirthdates?.length ?? 0);
  const displayedPossibilities = currentSlotPax
    ? possibilities.filter(room => {
        const min = room?.minCapacity ?? 0;
        const max = room?.maxCapacity ?? Infinity;
        return currentSlotPax >= min && currentSlotPax <= max;
      })
    : possibilities;
  const formattedPossibilities = formatHotelRoomPossibilities(
    displayedPossibilities,
  );

  // KD-3532 — the room the booking actually holds. `initialRoom` is written the
  // first time the modal is opened and then keeps pointing at the booked room
  // across re-openings, which is what the API validates the new pick against.
  const bookedRoom = roomComposition?.initialRoom ?? roomComposition?.room;
  const picksBookedRoom = isSameBedComposition(newBedroom, bookedRoom);

  // Leaving without confirming must leave the booking exactly as it was: the
  // pencil writes initialRoom/previousRoom *before* opening, and a leftover
  // initialRoom is enough to make the form submit a room change (BookingForm's
  // `if (currentRoomComp.initialRoom)`). Only clear them when nothing was
  // actually changed, so a pick confirmed in an earlier round-trip survives.
  const closeModal = () => {
    if (isSameBedComposition(roomComposition?.room, bookedRoom)) {
      setFieldValue(
        `roomCompositions.${selectedRoomIndex}.initialRoom`,
        undefined,
        false,
      );
      setFieldValue(
        `roomCompositions.${selectedRoomIndex}.previousRoom`,
        undefined,
        false,
      );
    }
    setIsOpen(false);
  };

  return booking ? (
    <Modal
      size="large"
      title={t('changeRoom')}
      isOpen
      onRequestClose={closeModal}
      header={
        <Stack direction="column" gutterSize={0.25}>
          <div>
            <Text color="text" fontWeight={700}>
              {t('hotelKeeperMessage') + ' :'}
            </Text>
          </div>
          <div style={{ textAlign: 'left' }}>
            <Text color="textLight">
              {booking?.hotelKeeperMessage
                ? '« ' + booking?.hotelKeeperMessage + ' »'
                : t('noMessage')}
            </Text>
          </div>
        </Stack>
      }
      footer={
        <Stack size="full" alignX="flex-end" alignY="center" gutterSize={2}>
          <KoobButton variant="secondary" type="button" onClick={closeModal}>
            {t('misc:cancel')}
          </KoobButton>
          <KoobButton
            variant="primary"
            type="button"
            disabled={currentIndex === undefined || picksBookedRoom}
            onClick={() => {
              setFieldValue(
                `roomCompositions.${selectedRoomIndex}.room`,
                newBedroom,
                true,
              );
              setIsOpen(false);
            }}
          >
            {t('room.confirmSelection')}
          </KoobButton>
        </Stack>
      }
    >
      <RoomCompositionContent
        isLoading={isLoading}
        selectedRooms={selectedRooms}
        availableRooms={formattedPossibilities}
        deleteRoom={deleteRoom}
        hotel={hotel}
        currentIndex={currentIndex}
        handleRoomIndexChange={handleRoomIndexChange}
        filters={filters}
        totalRoomNumber={totalRoomNumber}
        currentSelectedRoom={currentSelectedRoom}
        selectedRoomsFromPromotionSelect={selectedRoomsFromPromotionSelect}
        validateRoom={validateRoom}
        isOtherProposal={true}
        initialPossibilities={initialPossibilities}
        resquestId={data?.data?.requestId}
        promoList={promoList}
        updateRoomSupplements={updateRoomSupplements}
        bookedRoom={bookedRoom}
      />
    </Modal>
  ) : (
    <RoomCompositionContent
      isLoading={isLoading}
      selectedRooms={selectedRooms}
      availableRooms={formattedPossibilities}
      deleteRoom={deleteRoom}
      hotel={hotel}
      currentIndex={currentIndex}
      handleRoomIndexChange={handleRoomIndexChange}
      filters={filters}
      totalRoomNumber={totalRoomNumber}
      currentSelectedRoom={currentSelectedRoom}
      validateRoom={validateRoom}
      isOtherProposal={false}
      selectedRoomsFromPromotionSelect={selectedRoomsFromPromotionSelect}
      initialPossibilities={initialPossibilities}
      promoList={promoList}
      updateRoomSupplements={updateRoomSupplements}
    />
  );
};

export default HotelRoomComposition;
